import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EyeIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PasswordInput from '../ui/PasswordInput';
import { apiService } from '../../services/api';

interface MarketingVehicle {
  vehicle_id: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  images?: (string | { url: string; is_primary?: boolean })[];
  listing_platforms: string[];
  syndication_status: Record<string, any>;
  view_count: number;
  inquiry_count: number;
  days_on_lot?: number;
}

interface MarketingSyndicatorProps {
  vehicles: MarketingVehicle[];
  onSyndicate: (vehicleId: string, platforms: string[]) => Promise<void>;
}

interface FacebookPagePostResponse {
  vehicle_id: string;
  page_url?: string | null;
  title: string;
  post_text: string;
  images: string[];
}

interface FacebookAutoPostResponse {
  page_id: string;
  post_id?: string | null;
  post_url?: string | null;
  message: string;
  images: string[];
  status: string;
}

interface MarketplaceIntegrationConfig {
  platform: string;
  enabled: boolean;
  api_key_configured: boolean;
  api_key_last4?: string | null;
  connected: boolean;
}

type ViewMode = 'compact' | 'expanded' | 'list';

const MarketingSyndicator: React.FC<MarketingSyndicatorProps> = ({ vehicles, onSyndicate }) => {
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [facebookPageUrl, setFacebookPageUrl] = useState('');
  const [facebookPost, setFacebookPost] = useState<FacebookPagePostResponse | null>(null);
  const [facebookPostError, setFacebookPostError] = useState<string | null>(null);
  const [facebookPostLoading, setFacebookPostLoading] = useState(false);
  const [facebookAutoPost, setFacebookAutoPost] = useState<FacebookAutoPostResponse | null>(null);
  const [facebookAutoPostError, setFacebookAutoPostError] = useState<string | null>(null);
  const [facebookAutoPostLoading, setFacebookAutoPostLoading] = useState(false);
  const [marketplaceStatus, setMarketplaceStatus] = useState<
    Record<string, Record<string, string>>
  >({});
  const [lastStatusRefresh, setLastStatusRefresh] = useState<Date | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [marketplaceConfigs, setMarketplaceConfigs] = useState<
    Record<string, MarketplaceIntegrationConfig>
  >({});
  const [marketplaceKeyDrafts, setMarketplaceKeyDrafts] = useState<Record<string, string>>({});
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'expanded';
    const stored = localStorage.getItem('marketing_syndicator_view') as ViewMode | null;
    return stored && ['compact', 'expanded', 'list'].includes(stored) ? stored : 'expanded';
  });

  // Hidden per Backbone #7 v1 scope (Jose verdict 2026-06-09, north-star audit):
  // OfferUp + Craigslist have no public listing API and are explicitly NOT in
  // v1. Hide-not-delete (Principle D) — backend adapters stay; re-add the
  // entries here if the backbone ever pulls them into scope.
  const hiddenPlatformIds = useMemo(() => new Set(['craigslist', 'offerup']), []);

  const availablePlatforms = useMemo(
    () =>
      [
        { id: 'facebook', name: 'Facebook Business Page', icon: '📘' },
        { id: 'craigslist', name: 'Craigslist', icon: '📋' },
        { id: 'offerup', name: 'OfferUp', icon: '🧡' },
        { id: 'google_vehicles', name: 'Google Vehicle Listings', icon: '🧾' },
        { id: 'carsforsale', name: 'CarsForSale.com', icon: '💰' },
        { id: 'autotrader', name: 'AutoTrader', icon: '🚗' },
        { id: 'cargurus', name: 'CarGurus', icon: '🔍' },
        { id: 'cars_com', name: 'Cars.com', icon: '🏁' },
        { id: 'carfax', name: 'Carfax', icon: '📄' },
        { id: 'truecar', name: 'TrueCar', icon: '✅' },
      ].filter((platform) => !hiddenPlatformIds.has(platform.id)),
    [hiddenPlatformIds]
  );

  const paidPlatformIds = [
    'autotrader',
    'cargurus',
    'cars_com',
    'carsforsale',
    'carfax',
    'truecar',
  ];

  const getVehicleImage = (vehicle: MarketingVehicle) => {
    if (!vehicle.images || vehicle.images.length === 0) return null;

    // Handle both string[] and object[] formats
    const firstImage = vehicle.images[0];
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    // If it's an object with url property
    if (typeof firstImage === 'object' && 'url' in firstImage) {
      return (firstImage as any).url;
    }
    return null;
  };

  const mapMarketplaceStatus = useCallback((status?: string | null) => {
    switch ((status || '').toLowerCase()) {
      case 'posted':
        return 'active';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'error';
      case 'delisted':
        return 'delisted';
      case 'stubbed':
        return 'stubbed';
      default:
        return 'not_listed';
    }
  }, []);

  const getPlatformStatus = useCallback(
    (vehicle: MarketingVehicle, platformId: string) =>
      marketplaceStatus[vehicle.vehicle_id]?.[platformId] ||
      vehicle.syndication_status?.[platformId]?.status ||
      'not_listed',
    [marketplaceStatus]
  );

  /**
   * Returns true only when the platform has been OAuth-connected by the tenant.
   * Empty marketplaceConfigs (or a missing entry) defaults to false — safe.
   */
  const isPlatformConnected = useCallback(
    (platformId: string): boolean => marketplaceConfigs[platformId]?.connected ?? false,
    [marketplaceConfigs]
  );

  const marketplaceSummaries = useMemo(
    () =>
      vehicles.map((vehicle) => {
        const statuses = availablePlatforms.map((platform) =>
          mapMarketplaceStatus(getPlatformStatus(vehicle, platform.id))
        );
        const active = statuses.filter((status) => status === 'active').length;
        const pending = statuses.filter((status) => status === 'pending').length;
        const error = statuses.filter((status) => status === 'error').length;
        return {
          vehicleId: vehicle.vehicle_id,
          active,
          pending,
          error,
        };
      }),
    [vehicles, availablePlatforms, getPlatformStatus, mapMarketplaceStatus]
  );

  useEffect(() => {
    setFacebookPageUrl('');
    setFacebookPost(null);
    setFacebookPostError(null);
    setFacebookPostLoading(false);
    setFacebookAutoPost(null);
    setFacebookAutoPostError(null);
    setFacebookAutoPostLoading(false);
  }, [selectedVehicle]);

  useEffect(() => {
    refreshMarketplaceConfigs();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Live' };
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, text: 'Pending' };
      case 'error':
        return { color: 'bg-red-100 text-red-800', icon: ExclamationCircleIcon, text: 'Error' };
      case 'delisted':
        return { color: 'bg-gray-200 text-gray-700', icon: ClockIcon, text: 'Delisted' };
      case 'stubbed':
        return {
          color: 'bg-amber-100 text-amber-800',
          icon: ExclamationCircleIcon,
          text: 'Not Configured',
        };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: ClockIcon, text: 'Not Listed' };
    }
  };

  const refreshMarketplaceConfigs = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const response = await apiService.get<MarketplaceIntegrationConfig[]>(
        '/settings/marketplace-integrations'
      );
      const nextConfigs: Record<string, MarketplaceIntegrationConfig> = {};
      (response || []).forEach((config) => {
        nextConfigs[config.platform] = config;
      });
      setMarketplaceConfigs(nextConfigs);
    } catch (error) {
      setConfigError('Unable to load marketplace credentials.');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveMarketplaceConfig = async (platformId: string) => {
    const config = marketplaceConfigs[platformId];
    const apiKey = marketplaceKeyDrafts[platformId];
    try {
      await apiService.post('/settings/marketplace-integrations', {
        platform: platformId,
        enabled: config?.enabled ?? true,
        api_key: apiKey || undefined,
      });
      setMarketplaceKeyDrafts((prev) => ({ ...prev, [platformId]: '' }));
      await refreshMarketplaceConfigs();
    } catch (error) {
      setConfigError(`Unable to save credentials for ${platformId}.`);
    }
  };

  const refreshMarketplaceStatus = useCallback(async () => {
    if (!vehicles.length) return;
    setStatusLoading(true);
    try {
      // Use syndication_status from vehicle data instead of making separate API calls
      // The backend already provides this data in the marketing-status endpoint
      const nextStatus: Record<string, Record<string, string>> = {};

      vehicles.forEach((vehicle) => {
        const vehicleStatus: Record<string, string> = {};
        if (vehicle.syndication_status && typeof vehicle.syndication_status === 'object') {
          Object.entries(vehicle.syndication_status).forEach(([platform, status]) => {
            vehicleStatus[platform] = mapMarketplaceStatus(
              typeof status === 'string' ? status : (status as any)?.status
            );
          });
        }
        nextStatus[vehicle.vehicle_id] = vehicleStatus;
      });

      setMarketplaceStatus(nextStatus);
      setLastStatusRefresh(new Date());
    } finally {
      setStatusLoading(false);
    }
  }, [vehicles, mapMarketplaceStatus]);

  const handleSyndicate = async () => {
    if (!selectedVehicle || selectedPlatforms.length === 0) return;

    setLoading(true);
    try {
      if (selectedVehicle === 'bulk') {
        await apiService.post('/marketplaces/blast', {
          platforms: selectedPlatforms,
          dry_run: false,
          status_filter: 'available',
        });
      } else {
        await onSyndicate(selectedVehicle, selectedPlatforms);
      }
      setSelectedVehicle(null);
      setSelectedPlatforms([]);
      await refreshMarketplaceStatus();
    } catch (error) {
      console.error('Syndication failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platformId: string) => {
    // Hard gate: a disconnected platform cannot be selected (covers single + bulk).
    if (!isPlatformConnected(platformId)) return;
    setSelectedPlatforms((prev) =>
      prev.includes(platformId) ? prev.filter((id) => id !== platformId) : [...prev, platformId]
    );
  };

  const handleGenerateFacebookPost = async () => {
    if (!selectedVehicle || selectedVehicle === 'bulk') return;

    setFacebookPostLoading(true);
    setFacebookPostError(null);

    try {
      const response = await apiService.post<FacebookPagePostResponse>(
        `/marketplaces/facebook/page-post/${selectedVehicle}`,
        { page_url: facebookPageUrl || null }
      );
      setFacebookPost(response);
    } catch (error) {
      setFacebookPostError('Unable to generate Facebook page post.');
    } finally {
      setFacebookPostLoading(false);
    }
  };

  const handleCopyFacebookPost = async () => {
    if (!facebookPost?.post_text) return;
    await navigator.clipboard.writeText(facebookPost.post_text);
  };

  const handleAutoPostToFacebook = async () => {
    if (!selectedVehicle || selectedVehicle === 'bulk') return;

    setFacebookAutoPostLoading(true);
    setFacebookAutoPostError(null);

    try {
      const response = await apiService.post<FacebookAutoPostResponse>(
        `/marketplaces/facebook/pages/post/${selectedVehicle}`,
        {
          message: facebookPost?.post_text || undefined,
          use_ai: !facebookPost?.post_text,
        }
      );
      setFacebookAutoPost(response);
    } catch (error) {
      setFacebookAutoPostError('Unable to auto-post to Facebook.');
    } finally {
      setFacebookAutoPostLoading(false);
    }
  };

  useEffect(() => {
    refreshMarketplaceStatus();
    const interval = setInterval(() => {
      refreshMarketplaceStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [vehicles, refreshMarketplaceStatus]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('marketing_syndicator_view', viewMode);
    }
  }, [viewMode]);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <GlobeAltIcon className="w-5 h-5 mr-2 text-blue-500" />
            Online Marketing Dashboard
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-md bg-gray-100 p-1 text-xs">
              {(['compact', 'expanded', 'list'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-2 py-1 rounded ${
                    viewMode === mode
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
            <button
              onClick={refreshMarketplaceStatus}
              className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              {statusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
            <button
              onClick={() => setSelectedVehicle('bulk')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Bulk Syndicate
            </button>
          </div>
        </div>
        {lastStatusRefresh && (
          <p className="mt-2 text-xs text-gray-500">
            Last status update: {lastStatusRefresh.toLocaleTimeString()}
          </p>
        )}
      </div>

      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Paid Platform Credentials</h4>
            <p className="text-xs text-gray-500">
              Enable paid platforms and store API keys per tenant.
            </p>
          </div>
          <button
            onClick={refreshMarketplaceConfigs}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white rounded-md border border-gray-200 hover:bg-gray-100"
            disabled={configLoading}
          >
            {configLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {configError && <div className="mb-3 text-xs text-red-600">{configError}</div>}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {paidPlatformIds.map((platformId) => {
            const meta = availablePlatforms.find((platform) => platform.id === platformId);
            const config = marketplaceConfigs[platformId];
            const enabled = config?.enabled ?? false;
            const configured = config?.api_key_configured ?? false;
            const last4 = config?.api_key_last4 ? `••••${config.api_key_last4}` : null;

            return (
              <div key={platformId} className="rounded-md border border-gray-200 bg-white p-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:items-center">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{meta?.icon ?? '🔒'}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-800">
                        {meta?.name ?? platformId}
                      </span>
                      {configured && (
                        <span className="mt-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full w-fit">
                          Configured {last4 ?? ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <PasswordInput
                    value={marketplaceKeyDrafts[platformId] || ''}
                    onChange={(event) =>
                      setMarketplaceKeyDrafts((prev) => ({
                        ...prev,
                        [platformId]: event.target.value,
                      }))
                    }
                    placeholder={configured ? `API key configured ${last4 ?? ''}` : 'Enter API key'}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-xs"
                  />
                  <div className="flex flex-wrap items-center justify-start gap-3 md:justify-end">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      onClick={() => {
                        setMarketplaceConfigs((prev) => ({
                          ...prev,
                          [platformId]: {
                            platform: platformId,
                            enabled: !enabled,
                            api_key_configured: configured,
                            api_key_last4: config?.api_key_last4 ?? null,
                            connected: config?.connected ?? false,
                          },
                        }));
                      }}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                        enabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className="sr-only">Toggle enabled</span>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleSaveMarketplaceConfig(platformId)}
                      className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-3">Image</th>
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">VIN</th>
                  <th className="px-4 py-3">Views</th>
                  <th className="px-4 py-3">Inquiries</th>
                  <th className="px-4 py-3">Listings</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle) => {
                  const summary = marketplaceSummaries.find(
                    (item) => item.vehicleId === vehicle.vehicle_id
                  );
                  return (
                    <tr key={vehicle.vehicle_id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        {getVehicleImage(vehicle) ? (
                          <img
                            src={getVehicleImage(vehicle) as string}
                            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                            className="h-12 w-16 rounded-md object-cover"
                          />
                        ) : (
                          <div className="h-12 w-16 rounded-md bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{vehicle.vin}</td>
                      <td className="px-4 py-3">{vehicle.view_count}</td>
                      <td className="px-4 py-3">{vehicle.inquiry_count}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        Live {summary?.active ?? 0} · Pending {summary?.pending ?? 0} · Errors{' '}
                        {summary?.error ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedVehicle(vehicle.vehicle_id)}
                          className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          vehicles.map((vehicle) => (
            <div key={vehicle.vehicle_id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getVehicleImage(vehicle) ? (
                    <img
                      src={getVehicleImage(vehicle) as string}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="h-16 w-24 rounded-md object-cover"
                    />
                  ) : (
                    <div className="h-16 w-24 rounded-md bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                      No Image
                    </div>
                  )}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h4>
                    <p className="text-sm text-gray-500">VIN: {vehicle.vin}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-1">
                    <EyeIcon className="w-4 h-4 text-gray-400" />
                    <span>{vehicle.view_count} views</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 text-gray-400" />
                    <span>{vehicle.inquiry_count} inquiries</span>
                  </div>
                  {vehicle.days_on_lot && (
                    <span
                      className={`text-sm font-medium ${
                        vehicle.days_on_lot > 60
                          ? 'text-red-600'
                          : vehicle.days_on_lot > 30
                            ? 'text-yellow-600'
                            : 'text-green-600'
                      }`}
                    >
                      {vehicle.days_on_lot} days
                    </span>
                  )}
                </div>
              </div>

              {viewMode === 'compact' ? (
                <div className="flex flex-wrap gap-2 mb-4">
                  {availablePlatforms.map((platform) => {
                    const status = mapMarketplaceStatus(getPlatformStatus(vehicle, platform.id));
                    const badge = getStatusBadge(status);
                    return (
                      <div
                        key={platform.id}
                        className="flex items-center space-x-1 rounded-full bg-gray-50 px-2 py-1 text-xs"
                        title={`${platform.name}: ${badge.text}`}
                      >
                        <span>{platform.icon}</span>
                        <span className={`h-2 w-2 rounded-full ${badge.color.split(' ')[0]}`} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  {availablePlatforms.map((platform) => {
                    const status = getPlatformStatus(vehicle, platform.id);
                    const badge = getStatusBadge(status);
                    const StatusIcon = badge.icon;

                    return (
                      <div
                        key={platform.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{platform.icon}</span>
                          <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <StatusIcon className={`w-4 h-4`} />
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}
                          >
                            {badge.text}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedVehicle(vehicle.vehicle_id)}
                  className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                >
                  Manage Listings
                </button>
                <button className="px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors">
                  View Analytics
                </button>
                <button className="px-3 py-2 text-sm bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors">
                  Boost Listing
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Syndication Modal */}
      {selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Select Platforms to Syndicate</h3>

            <div className="space-y-3 mb-6">
              {availablePlatforms.map((platform) => {
                const connected = isPlatformConnected(platform.id);
                return (
                  <label
                    key={platform.id}
                    className={`flex items-center space-x-3 ${connected ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.id)}
                      onChange={() => togglePlatform(platform.id)}
                      disabled={!connected}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-lg">{platform.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                    {!connected && <span className="text-xs text-gray-400">Not connected</span>}
                  </label>
                );
              })}
            </div>

            {selectedPlatforms.includes('facebook') && (
              <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
                <h4 className="text-sm font-semibold text-blue-900">Facebook Business Page Post</h4>
                <p className="text-xs text-blue-700 mt-1">
                  Facebook Marketplace doesn’t allow business posting. Generate a ready-to-post
                  update for your business page.
                </p>

                <div className="mt-3 space-y-2">
                  <label className="block text-xs font-medium text-blue-900">
                    Business Page URL (optional)
                  </label>
                  <input
                    type="url"
                    value={facebookPageUrl}
                    onChange={(event) => setFacebookPageUrl(event.target.value)}
                    placeholder="https://www.facebook.com/YourBusinessPage"
                    className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm"
                  />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={handleGenerateFacebookPost}
                    disabled={facebookPostLoading || selectedVehicle === 'bulk'}
                    className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {facebookPostLoading ? 'Generating...' : 'Generate Page Post'}
                  </button>
                  {facebookPageUrl && (
                    <a
                      href={facebookPageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 text-xs font-medium text-blue-700 bg-white rounded-md border border-blue-200 hover:bg-blue-100"
                    >
                      Open Page
                    </a>
                  )}
                </div>

                {facebookPostError && (
                  <p className="mt-2 text-xs text-red-600">{facebookPostError}</p>
                )}

                {facebookPost && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-blue-900">Post Text</span>
                      <button
                        onClick={handleCopyFacebookPost}
                        className="text-xs text-blue-700 hover:underline"
                      >
                        Copy
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={facebookPost.post_text}
                      rows={5}
                      className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-xs"
                    />
                    {facebookPost.images.length > 0 && (
                      <div className="text-xs text-blue-800">
                        {facebookPost.images.length} image(s) ready to attach.
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-green-900">
                      Auto-post to Connected Page
                    </span>
                    <button
                      onClick={handleAutoPostToFacebook}
                      disabled={facebookAutoPostLoading || selectedVehicle === 'bulk'}
                      className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {facebookAutoPostLoading ? 'Posting...' : 'Post Now'}
                    </button>
                  </div>
                  {facebookAutoPostError && (
                    <p className="mt-2 text-xs text-red-600">{facebookAutoPostError}</p>
                  )}
                  {facebookAutoPost && (
                    <div className="mt-2 text-xs text-green-800">
                      <p>Status: {facebookAutoPost.status}</p>
                      {facebookAutoPost.post_url && (
                        <a
                          href={facebookAutoPost.post_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-green-700 hover:underline"
                        >
                          View Facebook Post
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedVehicle(null)}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSyndicate}
                disabled={loading || selectedPlatforms.length === 0}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Syndicating...' : `Syndicate to ${selectedPlatforms.length} platforms`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingSyndicator;
